import type { CampaignCouponTemplateRecord } from '@navio/nsdk';

export const DISCOUNT_USAGE_KIND = {
  once: 'once',
  limited: 'limited',
  forever: 'forever'
} as const;

type DiscountUsageKind = (typeof DISCOUNT_USAGE_KIND)[keyof typeof DISCOUNT_USAGE_KIND];

export type PartnerDiscountUsageFormValues = {
  usageKind: DiscountUsageKind;
  maxUsesCount: number | null;
};

export type DiscountTermsApi = {
  maxUses: number | null;
};

export const defaultPartnerDiscountUsageFormValues = (): PartnerDiscountUsageFormValues => ({
  usageKind: DISCOUNT_USAGE_KIND.once,
  maxUsesCount: 2
});

const maxUsesFromUsageKind = (usage: PartnerDiscountUsageFormValues): number | null =>
  usage.usageKind === DISCOUNT_USAGE_KIND.once
    ? 1
    : usage.usageKind === DISCOUNT_USAGE_KIND.limited
      ? (usage.maxUsesCount ?? 2)
      : null;

export const discountTermsFromPartnerUsage = (usage: PartnerDiscountUsageFormValues): DiscountTermsApi => ({
  maxUses: maxUsesFromUsageKind(usage)
});

export const partnerUsageFromTemplate = (template: CampaignCouponTemplateRecord): PartnerDiscountUsageFormValues => {
  const maxUses = template.defaultTerms.maxUses ?? null;
  return {
    usageKind:
      maxUses === 1
        ? DISCOUNT_USAGE_KIND.once
        : maxUses != null
          ? DISCOUNT_USAGE_KIND.limited
          : DISCOUNT_USAGE_KIND.forever,
    maxUsesCount: maxUses ?? 2
  };
};

export const formatMaxUsesLabel = (maxUses: number | null | undefined): string => {
  if (maxUses == null) {
    return 'No usage limit';
  }
  if (maxUses === 1) {
    return 'Can be used once';
  }
  return `Can be used ${maxUses} times`;
};

export const DISCOUNT_USAGE_OPTIONS = [
  {
    value: DISCOUNT_USAGE_KIND.once,
    label: 'Can be used once',
    description: 'Each customer can use this discount one time in total.'
  },
  {
    value: DISCOUNT_USAGE_KIND.limited,
    label: 'Can be used a set number of times',
    description: 'Choose how many times each customer can use this discount.'
  },
  {
    value: DISCOUNT_USAGE_KIND.forever,
    label: 'No usage limit',
    description: 'Customers can keep using this discount while they are part of the campaign.'
  }
] as const;
